import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, Calendar, Users, Settings, BookOpen, ChevronDown, Key,
} from 'lucide-react'
import Icon from '@mdi/react'
import { mdiBaseball } from '@mdi/js'  // Used in pitching nav item
import { cn } from '@/utils/cn'
import { useAuthStore } from '@/store/auth'
import { useLogout } from '@/hooks/useAuth'
import { LogOut } from 'lucide-react'

const navItems = [
  { to: '/dashboard',  label: 'Dashboard', icon: LayoutDashboard },
  { to: '/schedule',   label: 'Schedule',  icon: Calendar },
  { to: '/roster',     label: 'Roster',    icon: Users },
  { to: '/pitching',   label: 'Pitching',  icon: null, mdiPath: mdiBaseball },
  { to: '/rules',      label: 'Rules',     icon: BookOpen },
  { to: '/settings',   label: 'Settings',  icon: Settings },
]

export function Sidebar() {
  const user = useAuthStore((s) => s.user)
  const logout = useLogout()
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <aside className="hidden md:flex flex-col w-60 shrink-0 bg-brand-950 min-h-screen">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-white/10 flex justify-center">
        <img src="/diamondops_logo.png" alt="DiamondOps" className="h-10" />
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {navItems.map(({ to, label, icon: LucideIcon, mdiPath }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-white/60 hover:bg-white/10 hover:text-white',
              )
            }
          >
            {LucideIcon
              ? <LucideIcon className="h-5 w-5 shrink-0" />
              : <Icon path={mdiPath!} size={0.85} className="shrink-0" />
            }
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User footer */}
      <div className="px-3 py-4 border-t border-white/10 relative">
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
        >
          <div className="h-7 w-7 rounded-full bg-brand-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
            {user?.display_name?.[0]?.toUpperCase() ?? '?'}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-white text-xs font-medium truncate">{user?.display_name}</p>
            <p className="text-white/40 text-xs truncate">{user?.role}</p>
          </div>
          <ChevronDown className={`h-4 w-4 text-white/40 shrink-0 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown menu */}
        {menuOpen && (
          <div className="absolute bottom-16 left-3 right-3 bg-brand-900 border border-white/20 rounded-lg shadow-lg">
            <NavLink
              to="/change-password"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors first:rounded-t-lg"
            >
              <Key className="h-4 w-4" />
              Change Password
            </NavLink>
            <button
              onClick={() => {
                setMenuOpen(false)
                logout.mutate()
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors last:rounded-b-lg"
            >
              <LogOut className="h-4 w-4" />
              Log Out
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}
